import React, { useState, useEffect } from 'react';
import { reviewAPI } from '../services/api';
import StarRating from './StarRating';
import './ReviewsList.css';

const ReviewsList = ({ helperId, showTitle = true, limit = null }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, highest, lowest
  const [filterRating, setFilterRating] = useState('all'); // all, 5, 4, 3, 2, 1

  useEffect(() => {
    loadReviews();
  }, [helperId]);

  const loadReviews = async () => {
    if (!helperId) return;
    
    try {
      setLoading(true);
      const response = await reviewAPI.getHelperReviews(helperId);
      setReviews(response.data.reviews || []);
    } catch (err) {
      setError('Failed to load reviews');
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const sortReviews = (reviews, sortBy) => {
    const sorted = [...reviews];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'highest':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return sorted.sort((a, b) => a.rating - b.rating);
      default:
        return sorted;
    }
  };

  const filterReviews = (reviews, filterRating) => {
    if (filterRating === 'all') return reviews;
    return reviews.filter(review => review.rating === parseInt(filterRating));
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.rating]++;
    });
    return distribution;
  };

  const processedReviews = sortReviews(
    filterReviews(reviews, filterRating),
    sortBy
  );

  const displayReviews = limit ? processedReviews.slice(0, limit) : processedReviews;

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reviews-error">
        <p>❌ {error}</p>
      </div>
    );
  }

  const ratingDistribution = getRatingDistribution();
  const averageRating = getAverageRating();

  return (
    <div className="reviews-list">
      {showTitle && (
        <div className="reviews-header">
          <h3>⭐ Customer Reviews</h3>
          {reviews.length > 0 && (
            <div className="reviews-summary">
              <div className="average-rating">
                <StarRating 
                  rating={parseFloat(averageRating)} 
                  readonly={true} 
                  size="large"
                  showText={false}
                />
                <span className="rating-score">{averageRating}</span>
                <span className="review-count">({reviews.length} review{reviews.length !== 1 ? 's' : ''})</span>
              </div>
              
              {/* Rating Distribution */}
              <div className="rating-distribution">
                {[5, 4, 3, 2, 1].map(rating => (
                  <div key={rating} className="rating-bar">
                    <span className="rating-label">{rating}★</span>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{ 
                          width: `${reviews.length > 0 ? (ratingDistribution[rating] / reviews.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="rating-count">{ratingDistribution[rating]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reviews.length === 0 ? (
        <div className="no-reviews">
          <div className="no-reviews-icon">📝</div>
          <h4>No reviews yet</h4>
          <p>Be the first to review this helper!</p>
        </div>
      ) : (
        <>
          {/* Filters and Sorting */}
          {!limit && (
            <div className="reviews-controls">
              <div className="filter-group">
                <label htmlFor="sortBy">Sort by:</label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="control-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rating</option>
                  <option value="lowest">Lowest Rating</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label htmlFor="filterRating">Filter by rating:</label>
                <select
                  id="filterRating"
                  value={filterRating}
                  onChange={(e) => setFilterRating(e.target.value)}
                  className="control-select"
                >
                  <option value="all">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>
            </div>
          )}

          {/* Reviews List */}
          <div className="reviews-grid">
            {displayReviews.map((review) => (
              <div key={review._id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-avatar">
                      {getInitials(review.customerId?.name || 'Anonymous')}
                    </div>
                    <div className="reviewer-details">
                      <h5>{review.customerId?.name || 'Anonymous Customer'}</h5>
                      <p className="review-date">{formatDate(review.createdAt)}</p>
                    </div>
                  </div>
                  <div className="review-rating">
                    <StarRating 
                      rating={review.rating} 
                      readonly={true} 
                      size="small"
                      showText={false}
                    />
                  </div>
                </div>
                
                <div className="review-content">
                  <p>{review.comment}</p>
                  {review.wouldRecommend && (
                    <div className="recommendation">
                      <span className="recommend-badge">👍 Recommends this helper</span>
                    </div>
                  )}
                </div>
                
                {review.service && (
                  <div className="review-service">
                    <span className="service-tag">
                      Service: {review.service.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Show More Button */}
          {limit && processedReviews.length > limit && (
            <div className="show-more">
              <button className="show-more-btn">
                View All {processedReviews.length} Reviews
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsList; 